import { Alert, Box, Typography } from '@mui/material';
import { CircleCheck, CircleX, RefreshCw, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useMemo, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { createAdminApi, type AdminHealthCheck } from '@/admin/adminApi';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { useHttpClient } from '@/app/providers';
import { AccessDenied, BADGE_TONES, Badge, Button, EmptyState, ErrorState, Icon, LoadingState, Metric, PageHeader } from '@/ui/kit';

import { FAILED_MESSAGES_PATH } from './routes';
import { latencySentence, probeOf, verdictOf } from './healthPresentation';
import { useOperationsRefresh } from './useOperationsRefresh';

/**
 * Sante du service (P13) : ce que la sonde d'administration verifie.
 *
 * Cinq regles, chacune dite sur l'ecran ou testee :
 *
 * 1. **Une tuile par verification rendue**, dans l'ordre du serveur, et une sonde
 *    en echec ne masque jamais les autres : le serveur repond toujours 200, et
 *    une sonde qui leve devient `failed` de son cote.
 * 2. **Le `detail` s'affiche tel quel.** C'est une chaine technique ; l'analyser
 *    (version de migration, nombre de messages) fabriquerait un chiffre que le
 *    serveur ne rend pas.
 * 3. **Aucun rafraichissement automatique** (ADR 0035 §4) : « Vérifier
 *    maintenant » et le retour sur l'onglet (≥ 30 s) relisent ; l'heure de la
 *    derniere lecture est ecrite pour qu'on sache ce qu'on regarde.
 * 4. **Latence de l'API et sauvegardes ne sont pas rendues** : le serveur n'expose
 *    ni serie de latence ni etat des sauvegardes (BO-23, #520). Pas de bloc vide
 *    qui promettrait une donnee absente.
 * 5. **Un 403 n'est pas une panne** et ne deconnecte pas : le role `support`
 *    suffit, un compte sans role lit une explication.
 */

const STATUS_ICONS: Readonly<Record<string, LucideIcon | undefined>> = {
  ok: CircleCheck,
  degraded: TriangleAlert,
  failed: CircleX,
};

const TIME = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const StatusMark = ({ check }: { check: AdminHealthCheck }): ReactElement => {
  // Memes couples encre/aplat que les badges : gardes a 4,5:1 dans les deux modes.
  const { ink, fill } = BADGE_TONES[verdictOf(check.status).tone];

  return (
    // Decoratif : le mot du verdict est ecrit a cote, la couleur ne porte pas seule.
    <Box aria-hidden sx={{ display: 'inline-flex', p: 1, borderRadius: '10px', bgcolor: `tokens.${fill}`, color: `tokens.${ink}` }}>
      <Icon icon={STATUS_ICONS[check.status] ?? TriangleAlert} size={20} />
    </Box>
  );
};

const CheckTile = ({ check }: { check: AdminHealthCheck }): ReactElement => {
  const probe = probeOf(check.name);
  const latency = latencySentence(check.latencyMs);

  return (
    <Metric
      label={probe.label}
      icon={probe.icon}
      status={<StatusMark check={check} />}
      value={verdictOf(check.status).label}
      caption={
        <>
          {/* Tel que le serveur l'ecrit : jamais analyse. */}
          <Box component="span" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
            {check.detail}
          </Box>
          {latency === null ? null : <Box component="span" sx={{ display: 'block' }}>{latency}</Box>}
        </>
      }
    />
  );
};

export const HealthScreen = (): ReactElement => {
  const http = useHttpClient();
  const navigate = useNavigate();
  const api = useMemo(() => createAdminApi(http), [http]);
  const { token, refresh } = useOperationsRefresh();

  const read = useAdminQuery(
    'health',
    async () => ({ health: await api.health(), readAt: new Date() }),
    { token },
  );

  const result = read.data;
  const failure = read.failure;

  return (
    <Box sx={{ display: 'grid', gap: 2.5, mt: 0.5, mb: 4 }}>
      <PageHeader
        eyebrow="Exploitation"
        title="Santé du service"
        subtitle="Ce que vérifie la sonde d’administration. Chaque lecture est inscrite au journal d’audit : l’écran ne se rafraîchit pas seul."
        badges={
          result === null ? undefined : (
            <Badge label={verdictOf(result.health.status).overall} tone={verdictOf(result.health.status).tone} dot />
          )
        }
        actions={
          <Button icon={RefreshCw} onClick={refresh} disabled={read.busy}>
            Vérifier maintenant
          </Button>
        }
      />

      {failure?.forbidden === true ? (
        <AccessDenied title="Votre rôle ne permet pas de consulter la santé du service">
          Votre compte reste connecté : il manque le rôle d’administration requis (au moins « Support »). Un rôle
          s’attribue depuis le serveur, jamais depuis cet écran.
        </AccessDenied>
      ) : null}

      {failure !== null && !failure.forbidden && result === null ? <ErrorState message={failure.message} onRetry={refresh} /> : null}

      {failure !== null && !failure.forbidden && result !== null ? (
        <Alert severity="error" variant="outlined" role="alert">
          La relecture a échoué : {failure.message} Les valeurs affichées datent de la lecture précédente.
        </Alert>
      ) : null}

      {read.busy && result === null && failure === null ? <LoadingState label="Vérification du service…" /> : null}

      {result === null ? null : result.health.checks.length === 0 ? (
        <EmptyState title="Aucune vérification" description="La sonde n’a renvoyé aucune vérification. Ce n’est pas un verdict : réessayez." />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {result.health.checks.map((check) => (
              <CheckTile key={check.name} check={check} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography role="status" sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
              Lu à {TIME.format(result.readAt)}
              {read.busy ? ' · relecture en cours…' : ''}
            </Typography>
            <Button
              variant="ghost"
              onClick={() => {
                void navigate(FAILED_MESSAGES_PATH);
              }}
            >
              Voir les traitements en erreur
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
